"use client";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useProviders } from "../../hooks/useProviders";
import { useSchedules } from "../../hooks/useSchedules";
import { useSearchParams, useRouter, useParams } from "next/navigation";
import { ClinicRow } from "../../types/Clinic";
import { ProviderRow } from "../../types/Provider";
import { fmtYM, fmtYMD, formatBlockTimeRange } from "../../lib/date-time";
import { SlotSelector } from "./SlotSelector";
import { BookingForm } from "./BookingForm";
import { BookingFormFields, BookReqBody } from "./types";
import { BookingSuccess } from "./BookingSuccess";
import {
  INVALID_PHONE_MESSAGE,
  normalizeEmail,
  normalizePhone,
} from "../../lib/normalize";
import {
  CONTACT_REQUIRED,
  contactProblem,
  publicContactHint,
} from "../../lib/patientContact";
import {
  CONSENT_CHANGED,
  CONSENT_REQUIRED,
  SLOT_TAKEN,
} from "../../lib/bookingRules";
import SelectDropdown from "../../components/SelectDropdown";
import Spinner from "../../components/Spinner";
import { publicRoutes } from "../../lib/api-client/publicRoutes";
import { apiFetch } from "../../lib/api-client/base";
import ClinicProfile from "./ClinicProfile";
import BookingDisabledNotice from "./BookingDisabledNotice";
import { BlockTime } from "../../types/BlockTime";
import Button from "@/app/components/Button";
import { DateTime } from "luxon";
import { CLINIC_CSS_THEMES } from "../../lib/themes";

const TZ = "Europe/Bratislava";
const CONSENT_REQUIRED_MESSAGE =
  "Pred odoslaním rezervácie potvrďte prosím podmienky ambulancie.";
const contactRequiredMessage = (smsEnabled: boolean) =>
  smsEnabled
    ? "Vyplňte prosím e-mail alebo telefón, aby sme vám mohli poslať potvrdenie."
    : "Vyplňte prosím e-mail, aby sme vám mohli poslať potvrdenie.";

function ClinicBookingPageInner({
  initialClinic,
  initialProviders,
  bookingDisabled,
}: {
  initialClinic?: ClinicRow | null;
  initialProviders?: ProviderRow[];
  bookingDisabled?: boolean;
}) {
  const router = useRouter();
  const params = useParams();

  // read clinic slug from dynamic route params
  const clinicSlug = useMemo(() => {
    try {
      const slug = (params?.clinicSlug as string) || "";
      return (slug || "").trim();
    } catch (error) {
      console.warn("Error reading route params:", error);
      return "";
    }
  }, [params]);

  // Get provider ID from query params (presence indicates skip clinic profile)
  const qp = useSearchParams();
  const queryProviderId = useMemo(() => {
    try {
      if (!qp) return "";
      return qp.get("provider") || qp.get("p") || "";
    } catch (error) {
      return "";
    }
  }, [qp]);

  // Clean up legacy query params (c/clinic) from URL after redirect
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const url = new URL(window.location.href);
      let changed = false;
      if (url.searchParams.has("c")) {
        url.searchParams.delete("c");
        changed = true;
      }
      if (url.searchParams.has("clinic")) {
        url.searchParams.delete("clinic");
        changed = true;
      }
      if (changed) {
        window.history.replaceState({}, "", url.toString());
      }
    } catch {}
  }, [qp]);

  // Show clinic profile first (unless provider is in URL)
  const [clinicProfileShown, setClinicProfileShown] =
    useState(!queryProviderId);

  // Handle browser back button to return to clinic profile
  useEffect(() => {
    const handlePopState = () => {
      const url = new URL(window.location.href);
      const hasProvider =
        url.searchParams.has("provider") || url.searchParams.has("p");

      // If provider param is removed, show clinic profile
      if (!hasProvider) {
        setClinicProfileShown(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const clinic = initialClinic ?? null;
  const smsEnabled = clinic?.sms_notifications_enabled ?? false;
  const contactHint = publicContactHint({
    smsEnabled,
    cancellationEnabled: clinic?.patient_cancellation_enabled ?? true,
    emailRemindersEnabled: clinic?.email_reminders_enabled ?? true,
    smsRemindersEnabled: clinic?.sms_reminders_enabled ?? false,
  });
  // Starts as the page loaded it; replaced if the clinic edits it while the
  // patient has the form open (see CONSENT_CHANGED in book()).
  const [consentText, setConsentText] = useState<string | null>(
    clinic?.booking_consent_text ?? null
  );

  // selections
  const [typeProviderId, setTypeProviderId] = useState<string>("");
  const [providerId, setProviderId] = useState<string>("");

  // calendar state
  const [month, setMonth] = useState<Date>();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [availableDays, setAvailableDays] = useState<Set<string>>(new Set());
  const [loadingFreeDays, setLoadingFreeDays] = useState(false);
  const [blockTimes, setBlockTimes] = useState<BlockTime[]>([]);
  const [loadingBlockTimes, setLoadingBlockTimes] = useState(false);
  const slotsRef = useRef<HTMLDivElement>(null);

  // slots
  const [slots, setSlots] = useState<string[]>([]);
  const [slotUTC, setSlotUTC] = useState<string>("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  // next-slot loading
  const [loadingNextSlot, setLoadingNextSlot] = useState(false);

  // booking in progress
  const [savingBooking, setSavingBooking] = useState(false);

  // All form state is now managed in BookingForm
  const [done, setDone] = useState<{
    bookingDone: boolean;
    hasEmail: boolean;
    email?: string;
    smsPhone?: string;
  }>({ bookingDone: false, hasEmail: false });

  // load clinics only when no clinic is preset
  // useEffect(() => {
  //   if (clinicSlug) return;
  //   (async () => {
  //     setLoadingClinic(true);
  //     const r = await fetch("/api/clinics")
  //       .then((x) => x.json())
  //       .catch(() => ({ items: [] }));
  //     const items = (r.items || []) as ClinicRow[];
  //     setClinics(items);
  //     setLoadingClinic(false);
  //   })();
  // }, [clinicSlug]);

  // Load providers with next_available_slot for display in ClinicProfile
  // (does not drive providerId — that is set only on user confirmation)
  const { providers: enrichedProviders, loading: loadingProviders } =
    useProviders({
      mode: "public",
      clinicId: clinic?.id,
      nextAvailableSlot: true,
    });
  // Preserve server order; only enrich next_available_slot once client fetch completes
  const providers = useMemo(() => {
    const initial = initialProviders ?? [];
    if (enrichedProviders.length === 0) return initial;
    const slotMap = new Map(
      enrichedProviders.map((p) => [p.id, p.next_available_slot])
    );
    return initial.map((p) => ({
      ...p,
      next_available_slot: slotMap.get(p.id) ?? p.next_available_slot,
    }));
  }, [enrichedProviders, initialProviders]);
  const { schedules, loading: loadingSchedules } = useSchedules({
    mode: "public",
    providerId: providerId,
  });

  // Set providerId from URL param on initial load (when clinic profile is skipped)
  useEffect(() => {
    if (queryProviderId) {
      setProviderId(queryProviderId);
    }
  }, [queryProviderId]);

  // fetch available days whenever month/type/provider changes
  useEffect(() => {
    (async () => {
      if (!typeProviderId || !providerId || !month) return;
      fetchAvailableDays();
    })();
  }, [typeProviderId, providerId, month, clinicSlug]); // include clinic

  // fetch block times whenever provider/month changes
  useEffect(() => {
    (async () => {
      if (!providerId || !month) return;
      await fetchBlockTimes();
    })();
  }, [providerId, month]);

  async function fetchAvailableDays() {
    if (!month) return;
    setLoadingFreeDays(true);
    const ym = fmtYM(month);
    try {
      const route = publicRoutes.freeDays.get(typeProviderId, providerId, ym);
      const result = await apiFetch(route);
      setAvailableDays(new Set(result.days || []));
    } catch (error) {
      console.error("Failed to fetch available days:", error);
      setAvailableDays(new Set());
    } finally {
      setLoadingFreeDays(false);
    }
  }

  async function fetchBlockTimes() {
    if (!month) return;
    setLoadingBlockTimes(true);
    const ym = fmtYM(month);
    try {
      const route = publicRoutes.blockTimes.get(providerId, ym);
      const result = await apiFetch(route);
      setBlockTimes(result.items || []);
    } catch (error) {
      console.error("Failed to fetch block times:", error);
      setBlockTimes([]);
    } finally {
      setLoadingBlockTimes(false);
    }
  }

  useEffect(() => {
    if (!slotUTC) return;

    // Push a new state to the history stack when a slot is selected
    window.history.pushState({ slotSelected: true }, "");

    // Scroll to top when slot is selected
    window.scrollTo({ top: 0, behavior: "smooth" });

    const onPopState = (event: PopStateEvent) => {
      // If user goes back and a slot was selected, clear it
      setSlotUTC("");
    };

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [slotUTC]);

  useEffect(() => {
    (async () => {
      if (!clinicSlug || !typeProviderId || !providerId) return;
      setLoadingNextSlot(true);
      try {
        // ask backend for earliest slot
        const route = publicRoutes.nextSlot.get(typeProviderId, providerId);
        const result = await apiFetch(route);

        if (!result.next_slot_utc) {
          // no availability in horizon -> clear selection
          setSelectedDate(undefined);
          setSlots([]);
          setSlotUTC("");
          return;
        }

        // set date from the returned slot
        const first = DateTime.fromISO(result.next_slot_utc, {
          zone: "utc",
        }).setZone(TZ);
        const y = first.year;
        const m = first.month;
        const d = first.day;

        // pick month/day
        const firstDay = new Date(y, m - 1, 1);
        // Only update month if it's different to avoid triggering unnecessary effects
        if (!month || month.getFullYear() !== y || month.getMonth() !== m - 1) {
          setMonth(firstDay);
        }
        setSelectedDate(new Date(y, m - 1, d));

        // load all slots for that day
        void loadSlotsFor(new Date(y, m - 1, d), false);
      } catch (error) {
        console.error("Failed to fetch next slot:", error);
        // Clear selections on error
        setSelectedDate(undefined);
        setSlots([]);
        setSlotUTC("");
      } finally {
        setLoadingNextSlot(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeProviderId, providerId, clinicSlug]);

  async function loadSlotsFor(date: Date, scrollToSlots = false) {
    if (!typeProviderId || !providerId) return;
    const ymd = fmtYMD(date);
    setLoadingSlots(true);
    try {
      const route = publicRoutes.slots.get(typeProviderId, ymd, providerId);
      const result = await apiFetch(route);
      setSlots(result.slots || []);
      setSlotUTC("");
      if (scrollToSlots) {
        setTimeout(() => {
          slotsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 200);
      }
    } catch (error) {
      console.error("Failed to fetch slots:", error);
      setSlots([]);
      setSlotUTC("");
    } finally {
      setLoadingSlots(false);
    }
  }

  function onDaySelect(d?: Date) {
    setSelectedDate(d);
    setSlots([]);
    setSlotUTC("");
    if (d) {
      void loadSlotsFor(d, true);
    }
  }

  async function book(formData: BookingFormFields) {
    if (!slotUTC) return alert("Vyberte čas");
    if (!typeProviderId || !providerId) return alert("Chýba typ alebo poskytovateľ");

    const {
      contactEmail,
      contactPhone,
      firstName,
      lastName,
      message,
      consentText: confirmedConsentText,
    } = formData;

    if (!firstName || !lastName) {
      return alert("Vyplňte všetky povinné polia");
    }

    if (contactPhone?.trim() && !normalizePhone(contactPhone)) {
      return alert(INVALID_PHONE_MESSAGE);
    }

    if (
      contactProblem({
        publicBooking: true,
        smsEnabled,
        email: normalizeEmail(contactEmail),
        phone: normalizePhone(contactPhone),
      })
    ) {
      return alert(contactRequiredMessage(smsEnabled));
    }

    if (consentText && !confirmedConsentText) {
      return alert(CONSENT_REQUIRED_MESSAGE);
    }

    setSavingBooking(true);

    try {
      const route = publicRoutes.appointments.post();
      await apiFetch(route, {
        typeProviderId,
        providerId,
        startUTCISO: slotUTC,
        contactEmail,
        contactPhone,
        firstName,
        lastName,
        message,
        consentText: confirmedConsentText,
      } as BookReqBody);

      setDone({
        bookingDone: true,
        hasEmail: !!contactEmail,
        email: contactEmail,
        // Only where an SMS actually went.
        smsPhone: smsEnabled ? contactPhone?.trim() || undefined : undefined,
      });
    } catch (error: unknown) {
      console.error("Booking failed:", error);
      const code = error instanceof Error ? error.message : "";
      // Taken or blocked while the form was open: back to a fresh slot list.
      if (code.includes(SLOT_TAKEN)) {
        alert("Vybraný termín už nie je voľný. Vyberte si prosím iný čas.");
        if (selectedDate) void loadSlotsFor(selectedDate);
        else setSlotUTC("");
        return;
      }
      // The clinic changed its text while the form was open. Show the new one
      // and have the patient tick it again — the form unticks on new text.
      if (code.includes(CONSENT_CHANGED)) {
        try {
          const { item } = await apiFetch(
            publicRoutes.clinics.getOne(clinic!.slug)
          );
          setConsentText(item.booking_consent_text ?? null);
          alert(
            "Ambulancia medzičasom upravila text na potvrdenie. Prečítajte si ho prosím a potvrďte znova."
          );
        } catch {
          alert(
            "Ambulancia medzičasom upravila text na potvrdenie. Obnovte prosím stránku."
          );
        }
        return;
      }
      // The clinic may require a message; say so rather than "Chyba pri
      // rezervácii", which gives the patient nothing to act on.
      alert(
        code.includes("MESSAGE_REQUIRED")
          ? "Táto ambulancia vyžaduje správu k rezervácii. Napíšte prosím dôvod návštevy."
          : code.includes(CONSENT_REQUIRED)
            ? CONSENT_REQUIRED_MESSAGE
            : code.includes("INVALID_PHONE")
            ? INVALID_PHONE_MESSAGE
            : code.includes(CONTACT_REQUIRED)
            ? contactRequiredMessage(smsEnabled)
            : "Chyba pri rezervácii"
      );
    } finally {
      setSavingBooking(false);
    }
  }

  // DayPicker: disable every day not in availableDays set
  const disabled = useMemo(
    () => ({
      dayMatcher: (d: Date) => !availableDays.has(fmtYMD(d)),
    }),
    [availableDays]
  );

  const resetBooking = () => {
    setDone({ bookingDone: false, hasEmail: false });
    setTypeProviderId("");
    setProviderId(providers.length === 1 ? providers[0].id : "");
    setMonth(() => {
      const now = DateTime.now().setZone(TZ);
      return new Date(now.year, now.month - 1, 1);
    });
    setSelectedDate(undefined);
    setAvailableDays(new Set());
    setSlots([]);
    setSlotUTC("");
    setLoadingSlots(false);

    // Remove provider from URL and show clinic profile again
    const url = new URL(window.location.href);
    url.searchParams.delete("provider");
    url.searchParams.delete("p");
    window.history.pushState({}, "", url.toString());
    setClinicProfileShown(true);
  };

  // Redirect to 404 when clinic is not found — must be in useEffect, not during render
  useEffect(() => {
    if (clinicSlug && !clinic) {
      router.push("/404");
    } else if (!clinicSlug) {
      router.push("/404");
    }
  }, [clinicSlug, clinic, router]);

  if (!clinic) {
    return <div style={{ padding: 20 }}>Presmerovávam...</div>;
  }

  // Subscription lapsed — online booking is off, show contact info instead.
  if (bookingDisabled) {
    return <BookingDisabledNotice clinic={clinic} />;
  }

  // ⬅️ If no clinic provided, show a simple picker and stop here
  // if (!clinicSlug) {
  //   return (
  //     <>
  //       {loadingClinic && <Spinner />}
  //       <ClinicPicker clinics={clinics} />
  //     </>
  //   );
  // }

  if (clinicProfileShown) {
    // Show loading spinner only if we have no clinic data yet
    if (!clinic) {
      return <Spinner />;
    }

    // Show clinic profile
    return (
      <ClinicProfile
        clinic={clinic}
        providers={providers}
        loadingProviders={loadingProviders}
        onContinue={(providerUrl: string) => {
          // Add provider to URL and hide clinic profile
          const url = new URL(window.location.href);
          if (providerUrl) {
            url.searchParams.set("provider", providerUrl);
            window.history.pushState({}, "", url.toString());
          }
          setProviderId(providerUrl);
          setClinicProfileShown(false);
        }}
      />
    );
  }

  // ── Themed flow wrapper ───────────────────────────────────────────────
  const step: 1 | 2 | 3 = done.bookingDone ? 3 : slotUTC ? 2 : 1;
  const flowTitles = [
    "Vytvoriť rezerváciu",
    "Rezervovanie termínu",
    "Rezervácia potvrdená",
  ];
  const flowTitle = flowTitles[step - 1];

  function handleFlowBack() {
    if (step === 3) {
      resetBooking();
    } else if (step === 2) {
      setSlotUTC("");
    } else {
      resetBooking();
    }
  }

  const t =
    CLINIC_CSS_THEMES[clinic!.theme || "blue"] || CLINIC_CSS_THEMES.blue;
  const wrapStyle: React.CSSProperties = {
    ...Object.entries(t).reduce((acc, [k, v]) => {
      (acc as Record<string, string>)[k] = v;
      return acc;
    }, {} as React.CSSProperties),
    position: "relative",
    overflow: "hidden",
    width: "100%",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: "var(--ink)",
    background: "var(--bg)",
    minHeight: "100vh",
  };

  const STEP_LABELS = ["Termín", "Údaje", "Hotovo"];

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <div style={wrapStyle}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(60% 50% at 4% -6%, var(--accent) 0%, transparent 58%), radial-gradient(52% 42% at 106% 6%, var(--accent) 0%, transparent 60%), radial-gradient(68% 52% at 98% 102%, var(--accent) 0%, transparent 62%)",
            opacity: 0.28,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(var(--accent) 1.5px, transparent 1.6px)",
            backgroundSize: "22px 22px",
            opacity: 0.18,
            WebkitMaskImage:
              "radial-gradient(140% 100% at 100% 0%, #000 0%, transparent 65%)",
            maskImage:
              "radial-gradient(140% 100% at 100% 0%, #000 0%, transparent 65%)",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 clamp(20px,5%,48px) 64px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Flow header */}
          <header
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "32px 0 28px",
            }}
          >
            <button
              onClick={handleFlowBack}
              style={{
                width: 42,
                height: 42,
                flexShrink: 0,
                border: "1px solid var(--line)",
                background: "var(--surface)",
                borderRadius: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--ink)",
                fontFamily: "inherit",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  color: "var(--accent)",
                }}
              >
                {clinic!.name}
              </div>
              <div
                data-testid="booking-flow-title"
                style={{
                  fontWeight: 800,
                  fontSize: "clamp(19px,5vw,24px)",
                  letterSpacing: "-.02em",
                  color: "var(--ink)",
                  lineHeight: 1.15,
                }}
              >
                {flowTitle}
              </div>
            </div>
          </header>

          {/* Step indicator */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 36,
              flexWrap: "wrap",
            }}
          >
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const active = n === step;
              const done_ = n < step;
              return (
                <React.Fragment key={n}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 13,
                        background:
                          active || done_ ? "var(--accent)" : "transparent",
                        color:
                          active || done_
                            ? "var(--accent-ink)"
                            : "var(--ink-mute)",
                        border:
                          active || done_
                            ? "none"
                            : "1.5px solid var(--ink-mute)",
                        flexShrink: 0,
                      }}
                    >
                      {n}
                    </span>
                    <span
                      style={{
                        fontWeight: active ? 700 : 400,
                        fontSize: 14,
                        color:
                          active || done_ ? "var(--ink)" : "var(--ink-mute)",
                      }}
                    >
                      {label}
                    </span>
                  </span>
                  {i < 2 && (
                    <span
                      style={{
                        flex: "0 0 20px",
                        height: 1,
                        background: "var(--line)",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step 1: slot selection */}
          {step === 1 && (
            <>
              <label
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: "var(--ink-soft)",
                  marginBottom: 10,
                  display: "block",
                }}
              >
                Vyberte typ vyšetrenia
              </label>
              <div style={{ position: "relative" }}>
                <select
                  value={typeProviderId}
                  onChange={(e) => setTypeProviderId(e.target.value)}
                  data-testid="appointment-type-dropdown"
                  style={{
                    width: "100%",
                    appearance: "none",
                    WebkitAppearance: "none",
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                    borderRadius: 13,
                    padding: "15px 44px 15px 16px",
                    fontFamily: "inherit",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "var(--ink)",
                    cursor: "pointer",
                  }}
                >
                  <option value="">Dôvod návštevy</option>
                  {(schedules ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    position: "absolute",
                    right: 15,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--ink-mute)",
                    pointerEvents: "none",
                  }}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </div>

              {/* Appointment type note */}
              {typeProviderId &&
                (() => {
                  const note = schedules?.find(
                    (s) => s.id === typeProviderId
                  )?.note;
                  return note ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 11,
                        background: "var(--accent-tint)",
                        borderRadius: 13,
                        padding: "14px 16px",
                        marginTop: 12,
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          color: "var(--accent-strong)",
                          flexShrink: 0,
                          marginTop: 1,
                        }}
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" />
                      </svg>
                      <div
                        style={{
                          fontSize: 13.5,
                          lineHeight: 1.55,
                          color: "var(--ink)",
                        }}
                      >
                        {note}
                      </div>
                    </div>
                  ) : null;
                })()}

              {/* Block time warnings — shown when type and month are selected */}
              {typeProviderId &&
                month &&
                blockTimes.map((bt) => (
                  <div
                    key={bt.id}
                    data-testid="block-time-message"
                    style={{
                      display: "flex",
                      gap: 11,
                      background: "var(--warn-tint)",
                      borderRadius: 13,
                      padding: "14px 16px",
                      marginTop: 12,
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        color: "var(--warn)",
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <path d="M12 9v4M12 17h.01" />
                    </svg>
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "var(--warn)",
                          marginBottom: 3,
                        }}
                      >
                        {formatBlockTimeRange(bt)}
                      </div>
                      <div
                        style={{
                          fontSize: 13.5,
                          lineHeight: 1.55,
                          color: "var(--ink)",
                        }}
                      >
                        {bt.message}
                      </div>
                    </div>
                  </div>
                ))}

              {typeProviderId && month && (
                <SlotSelector
                  typeId={typeProviderId}
                  month={month}
                  setMonth={setMonth}
                  selectedDate={selectedDate}
                  onDaySelect={onDaySelect}
                  disabled={disabled}
                  slots={slots}
                  selectedSlot={slotUTC}
                  setSlotUTC={setSlotUTC}
                  slotsContainerRef={slotsRef}
                />
              )}
            </>
          )}

          {/* Step 2: form */}
          {step === 2 && (
            <BookingForm
              slotUTC={slotUTC}
              providers={providers}
              providerId={providerId}
              setSlotUTC={setSlotUTC}
              onBook={book}
              messageRequired={clinic?.patient_message_required ?? false}
              consentText={consentText}
              smsEnabled={smsEnabled}
              contactHint={contactHint}
            />
          )}

          {/* Step 3: done */}
          {step === 3 && (
            <BookingSuccess
              slotUTC={slotUTC}
              providers={providers}
              providerId={providerId}
              onNewBooking={resetBooking}
              hasMail={done.hasEmail}
              email={done.email}
              smsPhone={done.smsPhone}
            />
          )}

          {(loadingFreeDays ||
            loadingNextSlot ||
            loadingSlots ||
            loadingBlockTimes ||
            loadingSchedules ||
            savingBooking) && <Spinner />}
        </div>
      </div>
    </>
  );
}

export default function ClinicBookingClient({
  initialClinic,
  initialProviders,
  bookingDisabled,
}: {
  initialClinic?: ClinicRow | null;
  initialProviders?: ProviderRow[];
  bookingDisabled?: boolean;
}) {
  return (
    <Suspense fallback={<div style={{ padding: 20 }}>Načítavam…</div>}>
      <ClinicBookingPageInner
        initialClinic={initialClinic}
        initialProviders={initialProviders}
        bookingDisabled={bookingDisabled}
      />
    </Suspense>
  );
}
